/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { NativeShareItem, NativeShareShareReceived } from '@rea.ch/capacitor-native-share';
import { BehaviorSubject, firstValueFrom, map, Subject } from 'rxjs';

import { EdgeTypeEnum } from '@reach/interfaces/edge-types/edge-types.types';
import { CreateEdgeRequestInterface } from '@reach/interfaces/edges/edges.requests';
import { NodeCreationEdgeInterface } from '@reach/interfaces/edges/edges.types';
import { ErrorCodes } from '@reach/interfaces/error/error-codes.enum';
import type { CreateFileError } from '@reach/interfaces/files/files.responses';
import { NodeCreationSource } from '@reach/interfaces/nodes/node-creation-source.interface';
import { NodeType } from '@reach/interfaces/nodes/node-type.enum';
import { GetNodeResponse } from '@reach/interfaces/nodes/nodes.responses';
import { WorkspaceCreateBody } from '@reach/interfaces/workspaces/workspace.requests';
import { ensure } from '@reach/utils/assertions/ensure';
import { ensureAssert } from '@reach/utils/assertions/ensure-assert';
import { normalizeString } from '@reach/utils/string/normalize-string';

import {
	EdgesApiService,
	FilesApiService,
	NotesApiService,
	TagsApiService,
	WebsitesApiService,
} from '~app-client/api/services';
import {
	QuotaService,
	ReachBlobDownloaderService,
	WorkspacesService,
} from '~app-client/core/services';
import { ReachNodeId } from '~app-client/core/types';
import {
	MenuPageKey,
	NodeCreationData,
	NodeCreationError,
	NodeCreationErrorKey,
	WebsiteTypes,
} from '~app-client/node-creation-new/types';
import { ReachWorkspace, ReachWorkspaceType, WorkspaceId } from '~app-client/workspaces/types';
import {
	isPersistedWorkspaceId,
	trashedWorkspaceId,
	unassignedWorkspaceId,
} from '~app-client/workspaces/utils';
import { RelationshipEdgeTypesService } from '~app-client-item/profile/_internal/relationship/services/relationship-edge-types.service';
import { asApiError, ReachError } from '~ng-common-lib/core/api/errors';
import { ReachStorage } from '~ng-common-lib/core/utils/storage';

import { NodeCreationStore } from './node-creation.store';

function getClientFileBasename(file: File): string {
	const fullName = file.webkitRelativePath || file.name;
	// Keep only the last segment so folder uploads match the basename returned by the API.
	return fullName.split(/[\\/]/).pop() ?? file.name;
}

@Injectable({ providedIn: 'root' })
export class NewNodeCreationService {
	private readonly router = inject(Router);
	private readonly filesApi = inject(FilesApiService);
	private readonly notesApi = inject(NotesApiService);
	private readonly tagsApi = inject(TagsApiService);
	private readonly websiteApi = inject(WebsitesApiService);
	private readonly workspacesService = inject(WorkspacesService);
	private readonly edgesApi = inject(EdgesApiService);
	private readonly relationshipEdgeTypesService = inject(RelationshipEdgeTypesService);
	private readonly blobDownloader = inject(ReachBlobDownloaderService);
	protected readonly quotaService = inject(QuotaService);

	private readonly goBack$$ = new Subject<void>();
	public readonly goBack$ = this.goBack$$.asObservable();

	private readonly modalClosed$$ = new Subject<boolean>();
	public readonly modalClosed$ = this.modalClosed$$.asObservable();
	private readonly commonError$$ = new Subject<NodeCreationError>();
	public readonly commonError$ = this.commonError$$.asObservable();

	private readonly loading$$ = new BehaviorSubject(false);
	public readonly loading$ = this.loading$$.asObservable();

	public readonly selectedWorkspacesIds$ = this.workspacesService.activeWorkspaces$.pipe(
		map((wsList) => {
			const filtered = wsList.filter((w) => {
				return this.workspaceCanBeSelected(w);
			});
			return filtered.map((ws) => ws.id);
		})
	);

	private readonly nodeCreationState$$ =
		new BehaviorSubject<NodeCreationStore<MenuPageKey> | null>(null);
	public readonly nodeCreationState$ = this.nodeCreationState$$.asObservable();

	private fromFirstAction = false;
	private highlightedNodeType?: NodeType;

	constructor() {
		this.router.routerState.root.queryParamMap
			.pipe(takeUntilDestroyed())
			.subscribe((params) => {
				const type = params.get('type') as NodeType | undefined;
				if (type) {
					this.highlightedNodeType = type;
				}
			});
	}

	public goBack(): void {
		this.goBack$$.next();
	}

	public closeCreation(): void {
		this.modalClosed$$.next(true);
		this.nodeCreationState$$.next(null);
	}

	public async openCreation<KEY extends MenuPageKey | null>(
		key: KEY,
		fromShare = false,
		data?: NodeCreationData<KEY>,
		fromFirstAction?: boolean
	): Promise<void> {
		const selectedWorkspacesIds = await firstValueFrom(this.selectedWorkspacesIds$);
		const _fromFirstAction = fromFirstAction ?? this.fromFirstAction;
		const store = new NodeCreationStore<KEY>({
			key,
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			data: data ?? null,
			fromFirstAction: _fromFirstAction,
			workspacesIds: selectedWorkspacesIds,
			fromShare,
		});

		this.nodeCreationState$$.next(store as NodeCreationStore<MenuPageKey>);
	}

	public setFromFirstAction(fromFirstAction: boolean): void {
		this.fromFirstAction = fromFirstAction;
	}

	//#region SHARE
	public async handleShare(share: NativeShareShareReceived): Promise<void> {
		const items: NativeShareItem[] = [];

		for (let i = 0; i < share.length; i++) {
			const item = share.items[`${i}`];
			if (item.uri !== '' && item.mimeType !== '') {
				items.push({ ...item, text: '' });
			} else {
				const websiteUrl = this.getFirstWebsite(item.text || '');
				if (websiteUrl !== '') {
					items.push({ uri: '', mimeType: '', text: websiteUrl });
				}
			}
		}

		if (items.length > 0) {
			// proceed to share
			await this.innerShareHandler(items);
		}
	}

	private async innerShareHandler(items: NativeShareItem[]): Promise<void> {
		const files = items.filter((item) => item.uri !== '');
		const websites = items.filter((item) => item.text !== '');

		try {
			await this.quotaService.load();

			if (files.length > 0) {
				await this.handleFilesShare(files);
			} else if (websites.length > 0) {
				await this.handleWebsitesShare(websites);
			}
		} catch (error) {
			console.error(error);
		}
	}

	private async handleFilesShare(shareItems: NativeShareItem[]): Promise<void> {
		try {
			this.loading$$.next(true);

			const files: File[] = [];
			// eslint-disable-next-line @typescript-eslint/prefer-for-of
			for (let i = 0; i < shareItems.length; i++) {
				try {
					const item = shareItems[i];
					const decodedUri = decodeURIComponent(item.uri);
					const namePaths = decodedUri.split('/');
					const fileName = namePaths.pop() || 'shared-file';

					let blob: Blob;

					if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
						try {
							console.debug('Handling shared file on iOS', {
								uri: item.uri,
								decodedUri,
							});

							blob = await this.blobDownloader.getBlobFromNativeUri(
								item.uri,
								item.mimeType
							);
						} catch (error) {
							console.error(error);
							const url = Capacitor.convertFileSrc(item.uri);
							//console.log(`${item.uri} -> ${url} -> ${fileName}`);

							blob = await this.blobDownloader.getBlobFromUrl(url);
						}
					} else {
						const url = Capacitor.convertFileSrc(item.uri);
						//console.log(`${item.uri} -> ${url} -> ${fileName}`);

						blob = await this.blobDownloader.getBlobFromUrl(url);
					}

					const currentFile = new File([blob], fileName, {
						type: item.mimeType,
					});

					files.push(currentFile);
				} catch (e) {
					console.error(e);
				}
			}

			await this.openCreation(NodeType.FILE, true, { files });
		} finally {
			this.loading$$.next(false);
		}
	}

	private async handleWebsitesShare(sharedItems: NativeShareItem[]): Promise<void> {
		const url = sharedItems[0].text;
		await this.openCreation(NodeType.WEBSITE, true, {
			url,
			option: WebsiteTypes.WEBSITE,
		});
	}

	private getFirstWebsite(text: string): string {
		const regexpProtocol = '(https?://)?';
		const regexpWww = '(www\\.)?';
		const regexpDomain = '[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,10}';
		const regexpParams = '\\b([-a-zA-Z0-9@:%_\\+.,~#?&\\/\\/=]*)';

		const regexp = new RegExp(
			`${regexpProtocol}${regexpWww}${regexpDomain}${regexpParams}`,
			'gi'
		);

		// /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,10}\b([-a-zA-Z0-9@:%_\+.~#?&\/\/=]*)/gi

		const match = text.match(regexp);

		let url = match?.[0] || '';

		// Remove last commas and dots
		while ([',', '.'].includes(url.slice(-1))) {
			url = url.substring(0, url.length - 1);
		}

		return url;
	}
	//#endregion

	public async createNote(name: string, workspacesIds: WorkspaceId[]): Promise<GetNodeResponse> {
		const note = await this.notesApi.create(workspacesIds, name);

		this.workspacesService.increaseCount(workspacesIds);

		return {
			...note,
			nodeType: NodeType.NOTE,
			workspaceIds: workspacesIds,
			isUnassigned: workspacesIds.includes(unassignedWorkspaceId),
			isTrashed: workspacesIds.includes(trashedWorkspaceId),
			score: 0,
		};
	}

	public async errorHandler<T>(
		cb: () => Promise<T>,
		errorHandler?: (error: ReachError) => boolean,
		finishHandler?: () => void
	): Promise<T> {
		try {
			return await cb();
		} catch (error) {
			this.handleError(error, errorHandler);
			throw new Error('Unhandled error', { cause: error });
		} finally {
			finishHandler?.();
		}
	}

	private handleError(error: unknown, errorHandler?: (error: ReachError) => boolean): void {
		const reachError = asApiError(error);
		if (reachError.isReachError) {
			const handled = errorHandler?.(reachError.error) || false;
			if (!handled) {
				console.error(reachError.error);
				return this.throwError({ key: NodeCreationErrorKey.UNKNOWN, data: {} });
			}
		} else {
			return this.throwError({ key: NodeCreationErrorKey.UNKNOWN, data: {} });
		}
	}

	public throwError(error: NodeCreationError): void {
		this.commonError$$.next(error);
	}

	public async doWhileLoading<T>(cb: () => Promise<T>): Promise<T> {
		try {
			this.loading$$.next(true);
			const ret = await cb();
			return ret;
		} finally {
			this.loading$$.next(false);
		}
	}

	public async handleNodeCreationSuccess(node: GetNodeResponse): Promise<void> {
		if (this.highlightedNodeType?.length) {
			if (node.nodeType === NodeType.NOTE) {
				ReachStorage.setItem(
					ReachStorage.asStartupKey('nodeInfo_return_to_search'),
					node.id
				);

				void this.openNode(node);
				return;
			}

			history.replaceState(null, null as unknown as string, location.origin);
			await this.router.navigate(['/', 'home'], { queryParamsHandling: null });
		} else {
			void this.openNode(node);
		}
	}

	private async openNode(node: GetNodeResponse): Promise<void> {
		if (node.nodeType === NodeType.NOTE) {
			ReachStorage.setItem('__nodeInfo__lastContentKey', 'NOTE');
		}

		await this.router.navigate(['/', 'home', 'node', node.id]);
	}

	private workspaceCanBeSelected(ws: ReachWorkspace): boolean {
		return (
			!!ws && ws.type !== ReachWorkspaceType.TRASHED_NODES && isPersistedWorkspaceId(ws.id)
		);
	}

	// WORKSPACES
	public async createWorkspace(data: WorkspaceCreateBody): Promise<ReachWorkspace> {
		const existing = this.checkExistingWorkspace(data.name);
		if (existing) {
			throw Object.assign(new Error('Workspace already exists'), {
				code: ErrorCodes.WORKSPACES_ALREADY_EXISTS,
				meta: { name: data.name, existing },
			});
		}
		// eslint-disable-next-line no-useless-catch
		try {
			return await this.workspacesService.createWorkspace(data);
		} catch (error) {
			throw error;
		}
	}

	public checkExistingWorkspace(name: string): ReachWorkspace | null {
		const normalizedName = normalizeString(name);
		const existing = this.workspacesService.workspaces.find((ws) => {
			return normalizeString(ws.name) === normalizedName;
		});

		return existing || null;
	}

	// TAGS

	public async createTagByName(
		workspacesIds: WorkspaceId[],
		name: string,
		source: NodeCreationSource,
		relatedIds: ReachNodeId[]
	): Promise<GetNodeResponse> {
		const outgoingEdges: NodeCreationEdgeInterface[] =
			relatedIds.length === 0
				? []
				: relatedIds.map((otherNodeId) => ({
						otherNodeId,
						typeId: this.resolveEdgeTypeId(EdgeTypeEnum.LINKED),
					}));
		const node = await this.tagsApi.create(workspacesIds, name, source, outgoingEdges);

		this.workspacesService.increaseCount(workspacesIds);

		return {
			...node,
			nodeType: NodeType.TAG,
			workspaceIds: workspacesIds,
			isUnassigned: workspacesIds.includes(unassignedWorkspaceId),
			isTrashed: workspacesIds.includes(trashedWorkspaceId),
			score: 0,
		};
	}

	public async checkExistingTag(name: string): Promise<GetNodeResponse | undefined> {
		const existing = await this.tagsApi
			.checkExistence(name)
			.then((response) => {
				if (Array.isArray(response) && response.length > 0) {
					const item = response[0];

					return {
						...item,
						nodeType: NodeType.TAG,
						workspaceIds: item.workspaceIds,
						score: 0,
					} as unknown as GetNodeResponse;
				}

				throw new Error('Tag not found');
			})
			.catch(() => {
				return undefined;
			});

		return existing;
	}

	public async createEdge(
		source: ReachNodeId,
		target: ReachNodeId,
		typeId: EdgeTypeEnum
	): Promise<void> {
		const resolvedTypeId = this.resolveEdgeTypeId(typeId);
		await this.edgesApi.createMany([
			{
				sourceId: source,
				targetId: target,
				typeId: resolvedTypeId,
			},
		]);
	}

	private toOutgoingCreationEdges(
		edges: Array<Omit<CreateEdgeRequestInterface, 'sourceId'>>
	): NodeCreationEdgeInterface[] {
		return edges.map((edge) => ({
			otherNodeId: edge.targetId,
			typeId: this.resolveCreationEdgeTypeId(edge.typeId),
			description: edge.description,
			origin: edge.origin,
		}));
	}

	private resolveCreationEdgeTypeId(typeId: string): string {
		if ((Object.values(EdgeTypeEnum) as string[]).includes(typeId)) {
			return this.resolveEdgeTypeId(typeId as EdgeTypeEnum);
		}

		return typeId;
	}

	private resolveEdgeTypeId(typeId: EdgeTypeEnum): string {
		const edgeTypes = this.relationshipEdgeTypesService.$edgeTypes();

		const et = edgeTypes.find((edgeType) => edgeType.type === typeId)?.id;

		if (ensureAssert(et)) {
			return et;
		}

		return ensure(edgeTypes.find((edgeType) => edgeType.type === EdgeTypeEnum.LINKED)?.id);
	}

	// WEBSITES

	public async createWebsite(
		url: string,
		workspacesIds: WorkspaceId[],
		source: NodeCreationSource,
		edgesToCreate: Array<Omit<CreateEdgeRequestInterface, 'sourceId'>>,
		clientSideRendering?: boolean
	): Promise<GetNodeResponse> {
		const existing = await this.checkExistingWebsite(url);

		if (existing) {
			// eslint-disable-next-line @typescript-eslint/only-throw-error
			throw {
				code: NodeCreationErrorKey.WEBSITE_URL_EXISTS,
				data: { url, existing },
			};
		}
		const node = await this.websiteApi.create(
			url,
			workspacesIds,
			source,
			this.toOutgoingCreationEdges(edgesToCreate),
			clientSideRendering
		);

		this.workspacesService.increaseCount(workspacesIds);

		return {
			...node,
			nodeType: NodeType.WEBSITE,
			workspaceIds: workspacesIds,
			isUnassigned: false,
			isTrashed: false,
			score: 0,
		};
	}

	public async checkExistingWebsite(url: string): Promise<GetNodeResponse | undefined> {
		const existing = await this.websiteApi
			.checkExistence(url)
			.then((response) => {
				return {
					...response.website,
					workspaceIds: response.workspaceIds,
					nodeType: NodeType.WEBSITE,
					score: 0,
				} as GetNodeResponse;
			})
			.catch(() => {
				return undefined;
			});

		return existing;
	}

	// FILES

	public async createFiles(
		files: File[],
		workspacesIds: WorkspaceId[],
		source: NodeCreationSource
	): Promise<{ created: GetNodeResponse[]; failed: Array<CreateFileError & { file: File }> }> {
		const response = await this.filesApi.createFiles(files, workspacesIds, source);

		this.workspacesService.increaseCount(workspacesIds, response.files.length);

		const created = response.files.map((node): GetNodeResponse => {
			return {
				...node,
				nodeType: NodeType.FILE,
				workspaceIds: workspacesIds,
				isUnassigned: false,
				isTrashed: false,
				score: 0,
			};
		});

		const failed = response.errors.map((error) => {
			const matchingFile = ensure(
				files.find((file) => getClientFileBasename(file) === error.filename),
				(filename: string) => {
					throw new Error(`Could not match failed file ${filename}`);
				},
				error.filename
			);
			return {
				...error,
				file: matchingFile,
			};
		});

		return { created, failed };
	}
}
